/**
 * F17 T17.1.5 — DecisionNode: węzeł decyzyjny (akceptacja).
 *
 * Pisany od zera (SellCall ma osobny ApprovalNode + ConditionNode — nasz model
 * łączy: akceptacja + routing warunkowy na krawędziach wyjściowych).
 *
 * Wyświetla:
 * - Tytuł (z `data.label` lub fallback "Akceptacja")
 * - Typ akceptantów (5 typów z `approverTypeShort` i18n)
 * - Tryb decyzji (any / all / majority) — badge
 * - Warning gdy konfiguracja akceptantów jest niekompletna (czerwony wykrzyknik i czerwona
 *   ramka — wymóg publikacji, liczony `isApproverConfigComplete` z `@saas/shared`, tą samą regułą
 *   co serwer)
 * - Pluralizacja liczb akceptantów (i18next persons_one/_few/_many/_other dla PL)
 *
 * Handle: target top, source bottom.
 *
 * `data.isCurrent` (F17 T17.1.8c) — podświetlenie w read-only `ProcessGraphViewer`. Builder
 * nigdy tego pola nie ustawia (patrz `graphTypes.ts`), więc render w builderze bez zmian
 * (ring jest addytywny — nie nadpisuje `border-accent-200`/`border-red-300` z isConfigured).
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isApproverConfigComplete } from '@saas/shared';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { AlertCircle, UserCheck } from 'lucide-react';

import type { ApproverType, MultiApproverMode } from '@/lib/processes/constants';
import { CURRENT_NODE_RING_CLASS } from '@/lib/processes/step-tone';

interface ApproverConfig {
  type?: ApproverType;
  mode?: MultiApproverMode;
  userIds?: string[];
  roleTemplateIds?: string[];
}

interface DecisionNodeData {
  label?: string;
  approverConfig?: ApproverConfig;
  isCurrent?: boolean;
  [key: string]: unknown;
}

export const DecisionNode = memo(function DecisionNode({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  const nodeData = (data as DecisionNodeData) ?? {};
  const label = nodeData.label ?? t('tenant.processes.builder.nodes.decisionDefaultLabel');
  const approverConfig = nodeData.approverConfig;
  const approverType = approverConfig?.type;
  const mode = approverConfig?.mode ?? 'any';
  const isCurrent = nodeData.isCurrent ?? false;

  // Ta sama reguła co przy publikacji na serwerze — klocek nie świeci „gotowy", gdy serwer i tak
  // uzna etap za pozbawiony akceptantów (pusta pula osób / ról, adresat przy akceptancie
  // jednoosobowym itd.).
  const isConfigured = isApproverConfigComplete(approverConfig);

  return (
    <div
      className={`min-w-[200px] overflow-hidden rounded-lg border-2 bg-white shadow-lg transition-shadow dark:bg-gray-800 ${
        isConfigured
          ? 'border-accent-200 shadow-accent-500/10 dark:border-accent-700'
          : 'border-red-300 shadow-red-500/10 dark:border-red-700'
      } ${selected ? 'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-gray-900' : ''} ${
        isCurrent ? CURRENT_NODE_RING_CLASS : ''
      }`}
      data-testid="process-node-decision"
      data-current={isCurrent ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2 bg-accent-500 px-4 py-2">
        <UserCheck className="h-4 w-4 text-white" aria-hidden="true" />
        <span className="flex-1 truncate text-sm font-semibold text-white">{label}</span>
        {!isConfigured && (
          <AlertCircle
            className="h-4 w-4 text-red-200"
            aria-label={t('tenant.processes.builder.nodes.decisionMissingConfigAria')}
          />
        )}
        {isCurrent && <span className="sr-only">{t('tenant.processes.flow.currentStepAria')}</span>}
      </div>
      <div className="space-y-1.5 px-4 py-2.5">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {approverType
            ? t(`tenant.processes.builder.nodes.approverTypeShort.${approverType}`)
            : t('tenant.processes.builder.nodes.decisionConfigurePrompt')}
        </div>
        {isConfigured && (
          <div className="flex items-center gap-1">
            <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[9px] font-medium text-accent-700 dark:bg-accent-900/40 dark:text-accent-300">
              {t(`tenant.processes.builder.nodes.modeShort.${mode}`)}
            </span>
            {approverType === 'specific_users' && (approverConfig?.userIds?.length ?? 0) > 0 && (
              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                {t('tenant.processes.builder.nodes.personsCount', {
                  count: approverConfig?.userIds?.length ?? 0,
                })}
              </span>
            )}
            {approverType === 'roles' && (approverConfig?.roleTemplateIds?.length ?? 0) > 0 && (
              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                {t('tenant.processes.builder.nodes.rolesCount', {
                  count: approverConfig?.roleTemplateIds?.length ?? 0,
                })}
              </span>
            )}
          </div>
        )}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        className="!h-3 !w-3 !border-2 !border-white !bg-accent-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-3 !w-3 !border-2 !border-white !bg-accent-500"
      />
    </div>
  );
});
